/**
 * Trace context propagation utilities.
 *
 * Provides lightweight helpers for propagating W3C Trace Context headers
 * (traceparent / tracestate) across service boundaries — from frontend fetch
 * calls to backend API routes — without requiring the full OTel SDK in every
 * consumer.
 *
 * The frontend injects `traceparent` on every API request via the RUM fetch
 * wrapper. The backend reads it, creates a child span, and echoes the trace_id
 * back via `X-Trace-Id` response header so the frontend can correlate.
 */

// ---------------------------------------------------------------------------
// W3C Trace Context (traceparent header) — https://www.w3.org/TR/trace-context/
// ---------------------------------------------------------------------------

const TRACE_PARENT_HEADER = 'traceparent';
const TRACE_STATE_HEADER = 'tracestate';
const TRACE_ID_RESPONSE_HEADER = 'x-trace-id';

export interface TraceContext {
  readonly traceId: string;
  readonly spanId: string;
  readonly traceFlags: number;
  /** Raw traceparent header value, e.g. "00-<traceId>-<spanId>-01" */
  readonly traceparent: string;
}

// ---------------------------------------------------------------------------
// Parse
// ---------------------------------------------------------------------------

/**
 * Parse a W3C `traceparent` header value into its components.
 * Returns null if the header is absent or malformed.
 */
export function parseTraceparent(header: string | null | undefined): TraceContext | null {
  if (!header) return null;

  // Format: 00-<32hex traceId>-<16hex spanId>-<2hex flags>
  const parts = header.split('-');
  if (parts.length !== 4) return null;

  const [version, traceId, spanId, flags] = parts;
  if (version !== '00') return null;
  if (!/^[0-9a-f]{32}$/.test(traceId)) return null;
  if (!/^[0-9a-f]{16}$/.test(spanId)) return null;
  if (!/^[0-9a-f]{2}$/.test(flags)) return null;

  return {
    traceId,
    spanId,
    traceFlags: parseInt(flags, 16),
    traceparent: header,
  };
}

// ---------------------------------------------------------------------------
// Generate
// ---------------------------------------------------------------------------

/**
 * Generate a new random W3C traceparent header value.
 * Uses Web Crypto API (available in browser + Edge Runtime + Node 18+).
 */
export function generateTraceparent(): string {
  const bytes = (n: number): string => {
    const arr = new Uint8Array(n);
    crypto.getRandomValues(arr);
    return Array.from(arr, (b) => b.toString(16).padStart(2, '0')).join('');
  };

  const traceId = bytes(16); // 32 hex chars
  const spanId = bytes(8);   // 16 hex chars
  return `00-${traceId}-${spanId}-01`;
}

/**
 * Extract the trace ID from a traceparent string.
 * Returns null if invalid.
 */
export function extractTraceId(traceparent: string | null | undefined): string | null {
  return parseTraceparent(traceparent)?.traceId ?? null;
}

// ---------------------------------------------------------------------------
// HTTP header helpers
// ---------------------------------------------------------------------------

/**
 * Build request headers that inject the current trace context.
 * Pass the current `traceparent` value (from OTel active span or generated).
 */
export function buildTraceHeaders(
  traceparent: string,
  tracestate?: string,
): Record<string, string> {
  const headers: Record<string, string> = {
    [TRACE_PARENT_HEADER]: traceparent,
  };
  if (tracestate) {
    headers[TRACE_STATE_HEADER] = tracestate;
  }
  return headers;
}

/**
 * Extract the trace ID echoed back in the `X-Trace-Id` response header.
 * Returns null if not present.
 */
export function extractResponseTraceId(
  headers: Headers | Record<string, string>,
): string | null {
  if (headers instanceof Headers) {
    return headers.get(TRACE_ID_RESPONSE_HEADER);
  }
  return headers[TRACE_ID_RESPONSE_HEADER] ?? null;
}

// ---------------------------------------------------------------------------
// Fetch wrapper
// ---------------------------------------------------------------------------

export interface TracedFetchOptions extends RequestInit {
  /** Existing traceparent to continue a trace. Generates a new one if absent. */
  traceparent?: string;
  /** Optional tracestate to forward. */
  tracestate?: string;
}

/**
 * Thin wrapper around `fetch` that injects W3C trace context headers.
 * Suitable for use in environments where the full OTel SDK is not loaded
 * (e.g., service workers, lightweight utilities).
 *
 * Returns the response AND the trace ID used, so callers can log it.
 */
export async function tracedFetch(
  input: RequestInfo | URL,
  options?: TracedFetchOptions,
): Promise<{ response: Response; traceId: string }> {
  const { traceparent: existingTraceparent, tracestate, ...fetchOptions } = options ?? {};

  const traceparent = existingTraceparent ?? generateTraceparent();
  const traceId = extractTraceId(traceparent) ?? traceparent;

  const traceHeaders = buildTraceHeaders(traceparent, tracestate);
  const existingHeaders = fetchOptions.headers
    ? new Headers(fetchOptions.headers as HeadersInit)
    : new Headers();

  for (const [key, value] of Object.entries(traceHeaders)) {
    existingHeaders.set(key, value);
  }

  const response = await fetch(input, {
    ...fetchOptions,
    headers: existingHeaders,
  });

  return { response, traceId };
}

// Re-export header name constants for consumers
export { TRACE_PARENT_HEADER, TRACE_STATE_HEADER, TRACE_ID_RESPONSE_HEADER };
